import { useLayoutEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { CenteredSpinner } from '@components/ui/feedback';
import { billingSettingsPages } from '@features/billing-readiness/navigation/billingSettingsRoutes';
import { PracticeSettingsShell } from '@features/organizations/components/PracticeSettings/PracticeSettingsShell';
import { StartGroupSection } from '@features/organizations/components/StartGroupSection';
import { usePracticeSettingsItems } from '@features/organizations/hooks/usePracticeSettingsItems';
import {
  useGetOrganizationMembersQuery,
  useOrganizationAccess,
  useTransformMembers,
} from '@features/organizations/store';
import { useCan, useSubjectLanguageVariants } from '@features/user';
import { Alert, Box } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { scrollAppTo } from '@utils/appScroller';

/** Shared settings navigation; reports retain their dedicated console. */
export const PracticeLayout = () => {
  const { plural } = useSubjectLanguageVariants();
  const { userIsInGroup, orgLoading } = useOrganizationAccess();
  const canManageBilling = useCan('manage_billing');
  const { data } = useGetOrganizationMembersQuery(
    {},
    { skip: !userIsInGroup || !canManageBilling },
  );
  const { activeMembers } = useTransformMembers(data?.members);
  const items = usePracticeSettingsItems(activeMembers.length);
  const location = useLocation();
  const { navigation } = useEventTracking();
  useLayoutEffect(() => {
    scrollAppTo(0);
  }, [location.pathname]);
  const activePath = location.pathname.startsWith('/practice/billing-data-migration')
    ? billingSettingsPages['data-migration'].path
    : location.pathname;

  if (orgLoading) return <CenteredSpinner message="Loading practice data..." />;
  if (!userIsInGroup)
    return (
      <Box sx={{ p: 3 }}>
        <StartGroupSection />
      </Box>
    );
  if (!items.length)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          You don&apos;t have permission to manage this practice. Contact your practice
          administrator for access.
        </Alert>
      </Box>
    );

  return (
    <PracticeSettingsShell
      items={items}
      activePath={activePath}
      showPageHeading={
        !activePath.startsWith('/practice/settings') &&
        ![
          '/practice/appointment-types',
          '/practice/appointment-types/catalog',
          '/practice/appointment-labels',
          '/practice/note-template-catalog',
        ].includes(activePath)
      }
      workspace={[
        '/practice/note-template-catalog',
        '/practice/appointment-types/catalog',
      ].includes(activePath)}
      subtitle={
        activePath === billingSettingsPages['patient-payments'].path
          ? `Connect your payment account to collect payments from ${plural}.`
          : Object.values(billingSettingsPages).find(page => page.path === activePath)?.subtitle
      }
      title={Object.values(billingSettingsPages).find(page => page.path === activePath)?.label}
      onNavigate={item =>
        navigation.clicked({
          menu_item: item.path,
          source_path: activePath,
          destination_path: item.path,
          is_dropdown: false,
        })
      }
    >
      <Outlet />
    </PracticeSettingsShell>
  );
};
